import { ACCESS_MODEL, OTT_GLOBAL_PLAYER_ID } from '../constants';
import type { AccessModel, AppConfig, Config, GConfig } from '../../types/config';
import type { Settings } from '../../types/settings';
import type { LanguageDefinition } from '../../types/i18n';

import { createStore } from './utils';
import { branding_theme } from '../../../../platforms/web/src/config/themeConfig/branding';

type ConfigState = {
  loaded: boolean;
  config: Config;
  accessModel: AccessModel;
  settings: Settings;
  integrationType: string | null;
  supportedLanguages: LanguageDefinition[];
  featureFlags?: GConfig;
  appConfig: AppConfig;
  privacyPolicy: { en: string; es: string };
  termsOfUse: { en: string; es: string };
};

export const useConfigStore = createStore<ConfigState>('ConfigStore', () => ({
  loaded: false,
  featureFlags: {
    common: {
      default_language: 'en',
      supported_languages: ['en', 'es'],
      authentication_type: 'inplayer',
      cms_type: 'jwplayer',
      is_client_active: true,
      legal_docs: {
        privacy_policy: {
          en: '',
          es: '',
        },
        terms_of_use: {
          en: '',
          es: '',
        },
      },
      app_data: {
        support_email: '',
      },
    },
    inplayer: {
      client_id: '',
      drm_policy_id: '',
    },
    jwplayer: {
      config_id: '',
    },
    authentication: {
      is_authentication_enabled: true,
      is_qr_authentication_enabled: true,
      qr_settings: {
        show_registration_code: true,
        show_qr_image: false,
      },
      can_show_signin_button: true,
      can_show_signup_button: true,
      is_guest_mode_enabled: true,
      is_social_login_enabled: true,
      social_login_providers: {
        google: true,
        facebook: true,
        twitter: true,
        apple: true,
      },
      can_show_mobile_field: false,
    },
    feature_flags: {
      is_onboarding_screen_enabled: true,
      guest_streaming_enabled: false,
      ad_enabled: true,
      ad_settings: {
        ad_delivery_method: 'csai',
        ad_delivery_provider: 'jwp',
        ad_config_level: 'global',
      },
      is_subscription_enabled: true,
      is_search_enabled: true,
      is_favorite_enabled: true,
      is_continue_watching_enabled: false,
      is_recommended_content_enabled: true,
      is_download_enabled: true,
      is_drm_enabled: true,
      drm_settings: {
        drm_config_id: 'string',
        drm_live_media_id: 'string',
        drm_playlist_id: 'string',
        drm_policy_id: 'string',
      },
      player_type: 'jwp',
      player_analytics_enabled: false,
      is_analytics_enabled: true,
      analytics_provider: {
        firebase: true,
        adobe: false,
        appsflyer: false,
        google: true,
      },
      is_crash_analytics: true,
      is_epg_enabled: true,
      epg_disabled_message: 'This feature is not available in this Device',
      epg_settings: {
        max_days: 1,
        epg_no_program_message: 'No broadcast available',
        epg_empty_slot_message: 'No scheduled programs',
        epg_display_hours: ['00:00', '23:59'],
        epg_time_format: '12h',
        epg_default_image: 'string',
      },
      settings: {
        is_privacy_policy_enabled: true,
        is_terms_of_use_enabled: true,
        is_switch_profile_enabled: false,
        is_settings_enable: false,
        is_video_quality_enabled: true,
        analytics_settings: {
          abobe: {
            script_tag: '',
          },
        },
        is_autoplay_enabled: true,
        autoplay_default_value: 'off',
      },
      is_support_email: '',
      is_multi_language_support: false,
    },
    theme_colors: branding_theme,
    ui_settings: {
      hero_banner: 'option1',
      ui_flags: {
        is_splitup_fullname: true,
        is_lastname_required: false,
      },
    },
    platforms: {
      web: {
        is_client_active: true,
        latest_version: '1.0',
        activate_tv: true,
        export_account: true,
        delete_account: true,
        change_password: true,
        can_show_signin_button: true,
        can_show_signup_button: true,
        is_autoplay_enabled: true,
      },
      samsung: {
        is_client_active: true,
        latest_version: '1.0',
        force_update: true,
        show_buy_button: true,
        show_buy_button_alert: false,
        disable_buy_button: false,
        subscription_alert_msg: 'Please subscribe the item in Web',
        activate_tv: true,
        export_account: true,
        delete_account: true,
        change_password: true,
      },
      lg: {
        is_client_active: true,
        latest_version: '1.0',
        force_update: true,
        show_buy_button: true,
        show_buy_button_alert: false,
        disable_buy_button: false,
        subscription_alert_msg: 'Please subscribe the item in Web',
        activate_tv: true,
        export_account: true,
        delete_account: true,
        change_password: true,
      },
      vizio: {
        is_client_active: true,
        latest_version: '1.0',
        force_update: true,
        show_buy_button: true,
        show_buy_button_alert: false,
        disable_buy_button: false,
        subscription_alert_msg: 'Please subscribe the item in Web',
        activate_tv: true,
        export_account: true,
        delete_account: true,
        change_password: true,
      },
      android_mobile: {
        is_client_active: true,
        latest_version: '1.0',
        force_update: true,
        show_buy_button: true,
        show_buy_button_alert: false,
        disable_buy_button: false,
        subscription_alert_msg: 'Please subscribe the item in Web',
        activate_tv: true,
        export_account: true,
        delete_account: true,
        change_password: true,
      },
      android_tv: {
        is_client_active: true,
        latest_version: '1.0',
        force_update: true,
        show_buy_button: true,
        show_buy_button_alert: false,
        disable_buy_button: false,
        subscription_alert_msg: 'Please subscribe the item in Web',
        activate_tv: true,
        export_account: true,
        delete_account: true,
        change_password: true,
      },
      fire_tv: {
        is_client_active: true,
        latest_version: '1.0',
        force_update: true,
        show_buy_button: true,
        show_buy_button_alert: false,
        disable_buy_button: false,
        subscription_alert_msg: 'Please subscribe the item in Web',
        activate_tv: true,
        export_account: true,
        delete_account: true,
        change_password: true,
      },
      ios: {
        is_client_active: true,
        latest_version: '1.0',
        force_update: true,
        show_buy_button: true,
        show_buy_button_alert: false,
        disable_buy_button: false,
        subscription_alert_msg: 'Please subscribe the item in Web',
        activate_tv: true,
        export_account: true,
        delete_account: true,
        change_password: true,
      },
      tvos: {
        is_client_active: true,
        latest_version: '1.0',
        force_update: true,
        show_buy_button: true,
        show_buy_button_alert: false,
        disable_buy_button: false,
        subscription_alert_msg: 'Please subscribe the item in Web',
        activate_tv: true,
        export_account: true,
        delete_account: true,
        change_password: true,
      },
      roku: {
        is_client_active: true,
        latest_version: '1.0',
        force_update: true,
        show_buy_button: true,
        show_buy_button_alert: false,
        disable_buy_button: false,
        subscription_alert_msg: 'Please subscribe the item in Web',
        activate_tv: true,
        export_account: true,
        delete_account: true,
        change_password: true,
      },
    },
  },
  appConfig: {
    api: {
      base_url: '',
      end_point: {
        user: {
          get_user_profile: '',
          create_user_profile: '',
          delete_account: '',
          change_password: '',
          activate_tv: '',
        },
        favorite: {
          get_favorite: '',
          update_favorite: '',
          remove_favorite: '',
        },
        watch_history: {
          get_watch_history: '',
          update_watch_history: '',
          remove_watch_history: '',
        },
      },
    },
    branding: {
      logo_url: '',
      primary_font: '"Poppins", sans-serif',
      secondary_font: '"Poppins", sans-serif',
      favicon_url: '',
      client_site: '',
      support_email: '',
      video_horizontal_thumbnail: '',
      video_vertical_thumbnail: '',
    },
    user_profile: {
      personal_details: {
        first_name_last_name: {
          enabled: true,
          required: false,
        },
        company_name: {
          enabled: false,
          required: false,
        },
        address: {
          enabled: false,
          required: false,
        },
        phone_number: {
          enabled: false,
          required: false,
        },
        birth_date: {
          enabled: false,
          required: false,
        },
      },
    },
    video_details: {
      epg: {
        is_catchup_hours_enabled: false,
      },
    },
    ui_controls: {
      button_display_modes: {
        label_only: true,
        icon_only: true,
        label_with_icon: true,
      },
    },
  },
  config: {
    id: '',
    siteName: '',
    description: '',
    player: '',
    siteId: '',
    contentType: '',
    assets: {},
    content: [],
    menu: [],
    integrations: {
      cleeng: {
        useSandbox: true,
      },
      jwp: {
        clientId: null,
        assetId: null,
        useSandbox: true,
      },
    },
    styling: {},
  },
  settings: {
    additionalAllowedConfigSources: [],
    playerId: OTT_GLOBAL_PLAYER_ID,
  },
  supportedLanguages: [],
  accessModel: ACCESS_MODEL.AVOD,
  integrationType: null,
  privacyPolicy: { en: '', es: '' },
  termsOfUse: { en: '', es: '' },
}));
